import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests';
import { LanguageOptions } from '../../entities/LanguageOptions/index.js';
import { Availability } from '../../entities/Availability/index.js';
import { Costs } from '../../entities/Costs/index.js';
import { toast } from 'react-toastify';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { hasChangesAvailability } from '../../shared/utils/compareAvailability.js';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import axios from 'axios';
import { AddAdditionalPictures } from '../../features/CreateProductForm/AddAdditionalPictures.jsx';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { MeasurementsForm } from '../../shared/ui/MeasurementsForm/MeasurementsForm';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

const EditProductPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modificators, setModificators] = useState([]);
  const [additionalPictures, setAdditionalPictures] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [form, setForm] = useState(null);
  const [oldData, setOldData] = useState(null);
  
  // Состояние для модального окна подтверждения удаления
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const handleDeletePrice = (countryId) => {
    setForm((prev) => {
      const costs = prev.basePricing.costs.filter((i) => i.countryId !== countryId);
      return { ...prev, basePricing: { costs } };
    });
  };

  const handleChangeCost = (value) => {
    setForm((prev) => {
      const newCosts = prev.basePricing.costs.map((i) => {
        if (i.countryId === value.countryId) {
          return {
            ...i,
            costs: i.costs.map((z) => {
              if (z.currencyId === value.value.currencyId) {
                return {
                  ...z,
                  value: value.value.newValue,
                };
              } else {
                return z;
              }
            }),
          };
        } else {
          return i;
        }
      });

      return { ...prev, basePricing: { costs: newCosts } };
    });
  };

  const handleRequest = async () => {
    const body = new FormData();

    if (mainImage) {
      body.append('mainImg', mainImage);
    }

    if (hasChangesAvailability(oldData.availability, form.availability)) {
      const query = await apiRequests.products.updateAvailability(form.availability);
    }
    
    // Преобразуем данные формы для отправки на сервер
    const formData = JSON.parse(JSON.stringify(form));
    
    // Преобразуем SEOMetatags в seoMetatags для API
    if (formData.SEOMetatags) {
      formData.seoMetatags = {
        title: formData.SEOMetatags.Title,
        description: formData.SEOMetatags.Description,
        keywords: formData.SEOMetatags.Keywords
      };
      delete formData.SEOMetatags; // Удаляем оригинальное поле, чтобы избежать конфликта
    }
    
    console.log('Отправляемые данные:', formData);
    
    body.append('model', JSON.stringify(formData));
    const query = apiRequests.products.update(body);
    toast.promise(query, {
      pending: 'Запрос к серверу',
      error: 'Произошла ошибка',
      success: 'Товар успешно изменён',
    });
  };

  const getInitialPrices = useCallback(() => {
    if (!loading) {
      const result = form.basePricing.costs.map((i) => {
        const costs = i.costs.map((z) => {
          return { currencyId: z.currencyId, value: z.value };
        });
        return { countryId: i.countryId, costs };
      });

      return result;
    }
  }, [loading]);

  // Функция для открытия модального окна подтверждения удаления
  const handleDeleteImage = (imageId) => {
    setImageToDelete(imageId);
    setShowDeleteModal(true);
  };
  
  // Функция для подтверждения удаления изображения
  const confirmDeleteImage = () => {
    if (!imageToDelete) return;
    
    const query = apiRequests.products
      .deletePhoto(id, imageToDelete)
      .then((res) => {
        setAdditionalPictures((prev) => prev.filter((i) => i.id !== imageToDelete));
        // Закрываем модальное окно после удаления
        setShowDeleteModal(false);
        setImageToDelete(null);
      });
      
    toast.promise(query, {
      pending: 'Идёт запрос...',
      success: 'Изображение успешно удалено',
      error: 'Произошла ошибка',
    });
  };

  const handleAddImage = (value) => {
    const query = apiRequests.products
      .addPhoto(value, id)
      .then((res) => setAdditionalPictures((prev) => [...prev, res]));
    toast.promise(query, {
      pending: 'Идёт запрос...',
      success: 'Изображение успешно добавлено',
      error: 'Произошла ошибка',
    });
  };

  useEffect(() => {
    const getData = async () => {
      const product = await apiRequests.products.getById(id);
      console.log('Полученные данные о товаре:', product);
      
      // Всегда создаем объект SEOMetatags с правильной структурой
      product.SEOMetatags = {
        Title: product.seoMetatags?.title || '',
        Description: product.seoMetatags?.description || '',
        Keywords: product.seoMetatags?.keywords || ''
      };
      
      console.log('Установлены SEO-теги:', product.SEOMetatags);
      
      // Инициализируем measurements, если они отсутствуют
      if (!product.measurements) {
        product.measurements = {
          WeightInGramms: 0,
          HeightInMillimeters: 0,
          LengthInMillimeters: 0,
          WidthInMillimeters: 0
        };
        console.log('Созданы пустые measurements:', product.measurements);
      }
      setForm(product);
      setAdditionalPictures(product.images);
      const picturesQuery = product.images.map((i) =>
        axios.get(`${import.meta.env.VITE_API_URL}${i.imgPath}`),
      );
      setOldData(product);
      const categories = await apiRequests.products.getCategories();
      setCategories(categories);
      // const modificators = await apiRequests.products.getModificators()
      // setModificators(modificators)
      setLoading(false);
    };
    getData();
  }, []);

  if (loading) return <FormLoading />;

  const costs = getInitialPrices();

  return (
    <>
      <FormLayout
        onSubmit={handleRequest}
        onBack={() => navigate('/shop/products')}
        saving={saving}
        submitText="Сохранить"
        savingText="Сохранение..."
      >
        <FormSection title="Основная информация">
          <Row>
            <Col lg={8}>
              <Row>
                <Col md={8}>
                  <FormField label="Название" required>
                    <Form.Control
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="Введите название товара"
                    />
                  </FormField>
                </Col>
                <Col md={4}>
                  <FormField label="Артикул">
                    <Form.Control
                      type="text"
                      value={form.sku}
                      onChange={(e) => setForm((prev) => ({ ...prev, sku: e.target.value }))}
                      placeholder="SKU"
                    />
                  </FormField>
                </Col>
              </Row>

              <FormField label="Категория">
                <Form.Select
                  value={form.categoryId}
                  onChange={(e) => setForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                >
                  {categories.items?.map((i) => (
                    <option key={i.id} value={i.id}>{i.title}</option>
                  ))}
                </Form.Select>
              </FormField>

              <FormField label="Описание">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Введите описание товара"
                />
              </FormField>
            </Col>

            <Col lg={4}>
              <FormField label="Главное изображение">
                <UploadPicture
                  onSelect={setMainImage}
                  initialValue={form.mainImage.imgPath}
                  entityType="product"
                />
              </FormField>
            </Col>
          </Row>
        </FormSection>

        <FormSection title="Дополнительные изображения">
          <AddAdditionalPictures
            values={additionalPictures}
            onDelete={(value) => handleDeleteImage(value.id)}
            onAdd={handleAddImage}
          />
        </FormSection>

        <FormSection title="Цена">
          <Costs
            initialValues={costs}
            onDelete={handleDeletePrice}
            onChange={(value) => handleChangeCost(value)}
            onAdd={(value) =>
              setForm((prev) => ({
                ...prev,
                basePricing: { costs: [...prev.basePricing.costs, value] },
              }))
            }
          />
        </FormSection>

        <FormSection title="Габариты">
          <MeasurementsForm
            measurements={form.measurements}
            onChange={(measurements) => setForm((prev) => ({ ...prev, measurements }))}
          />
        </FormSection>

        <FormSection title="Настройки">
          <Row>
            <Col lg={6}>
              <LanguageOptions
                initialValue={form.mainLanguageId}
                onSelect={(val) => setForm((prev) => ({ ...prev, mainLanguageId: val }))}
              />
            </Col>
          </Row>

          <div className="mt-4">
            <Form.Label className="fw-medium mb-3">Доступность по странам</Form.Label>
            <Availability
              initialValues={form.availability}
              onSelect={(val) =>
                setForm((prev) => ({
                  ...prev,
                  availability: { ...prev.availability, availableInAllCountries: val },
                }))
              }
              onSelectAllowed={(val) =>
                setForm((prev) => ({
                  ...prev,
                  availability: { ...prev.availability, allowedCountriesIds: val },
                }))
              }
              onSelectDisallowed={(val) =>
                setForm((prev) => ({
                  ...prev,
                  availability: { ...prev.availability, disallowedCountriesIds: val },
                }))
              }
            />
          </div>
        </FormSection>

        <FormSection title="SEO настройки">
          <SEOMetatagsForm
            seoMetatags={form.SEOMetatags}
            onChange={(metaTags) => setForm((prev) => ({ ...prev, SEOMetatags: metaTags }))}
          />
        </FormSection>
      </FormLayout>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Подтверждение удаления</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Вы действительно хотите удалить это изображение?</p>
          <p className="text-danger">Это действие нельзя отменить.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Отмена</Button>
          <Button variant="danger" onClick={confirmDeleteImage}>Удалить</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditProductPage;
